import React from 'react';
import { Link } from 'react-router-dom';
import { Calendar, Clock, ArrowRight, FileText } from 'lucide-react';
import { Button } from '../components/ui/button';

const BlogPage = () => {
  const blogPosts = [
    {
      id: 'digital-governance-last-mile',
      title: 'Digital Governance at the Last Mile',
      excerpt: 'How AEPL is digitizing rural development in Central India with Zoho - transforming manual governance into a transparent, data-driven operation.',
      category: 'Case Study',
      date: 'November 2024',
      readTime: '8 min read',
      image: 'https://images.unsplash.com/photo-1454165804606-c3d57bc86b40',
      tags: ['GovTech', 'Rural Development', 'Digital Transformation']
    }
  ];

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="relative pt-32 pb-20 bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 text-white overflow-hidden">
        <div className="absolute top-0 right-0 w-96 h-96 bg-cyan-500/10 rounded-full blur-3xl" />
        <div className="absolute bottom-0 left-0 w-96 h-96 bg-blue-500/10 rounded-full blur-3xl" />
        
        <div className="relative z-10 container-custom text-center">
          <div className="inline-block bg-cyan-500/20 border border-cyan-500/30 text-cyan-300 px-4 py-2 rounded-full text-sm font-medium mb-6">
            <FileText className="w-4 h-4 inline mr-2" />
            Insights & Case Studies
          </div>
          <h1 className="text-5xl md:text-6xl font-bold mb-6">
            AEPL Blog
          </h1>
          <p className="text-xl text-gray-300 max-w-2xl mx-auto">
            Real stories of impact, technology, and transformation in rural governance
          </p>
        </div>
      </section>

      {/* Blog Posts Grid */}
      <section className="section-padding bg-white">
        <div className="container-custom">
          <div className="max-w-6xl mx-auto">
            <div className="grid gap-8">
              {blogPosts.map((post) => (
                <Link
                  key={post.id}
                  to={`/blog/${post.id}`}
                  className="group bg-white rounded-3xl overflow-hidden shadow-lg hover:shadow-2xl transition-all duration-500 border border-gray-100 hover:border-cyan-200"
                >
                  <div className="grid md:grid-cols-5 gap-0">
                    {/* Image */}
                    <div className="md:col-span-2 relative h-64 md:h-auto overflow-hidden">
                      <div
                        className="absolute inset-0 bg-cover bg-center group-hover:scale-105 transition-transform duration-700"
                        style={{ backgroundImage: `url(${post.image})` }}
                      />
                      <div className="absolute top-4 left-4">
                        <span className="bg-cyan-500 text-white px-4 py-1.5 rounded-full text-sm font-medium">
                          {post.category}
                        </span>
                      </div>
                    </div>

                    {/* Content */}
                    <div className="md:col-span-3 p-8 md:p-12 flex flex-col justify-between">
                      <div>
                        <div className="flex items-center gap-4 text-sm text-gray-500 mb-4">
                          <div className="flex items-center gap-1">
                            <Calendar className="w-4 h-4" />
                            <span>{post.date}</span>
                          </div>
                          <div className="flex items-center gap-1">
                            <Clock className="w-4 h-4" />
                            <span>{post.readTime}</span>
                          </div>
                        </div>

                        <h2 className="text-3xl font-bold mb-4 text-gray-900 group-hover:text-cyan-600 transition-colors">
                          {post.title}
                        </h2>

                        <p className="text-lg text-gray-600 leading-relaxed mb-6">
                          {post.excerpt}
                        </p>

                        <div className="flex flex-wrap gap-2 mb-6">
                          {post.tags.map((tag, idx) => (
                            <span
                              key={idx}
                              className="bg-gray-100 text-gray-700 px-3 py-1 rounded-full text-sm"
                            >
                              {tag}
                            </span>
                          ))}
                        </div>
                      </div>

                      <div className="flex items-center text-cyan-600 font-medium group-hover:gap-2 transition-all">
                        Read Full Article
                        <ArrowRight className="w-5 h-5 ml-2 group-hover:translate-x-1 transition-transform" />
                      </div>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="section-padding bg-gradient-to-br from-gray-50 to-white">
        <div className="container-custom max-w-4xl text-center">
          <h2 className="text-4xl font-bold mb-6 text-gray-900">
            Want to Learn More?
          </h2>
          <p className="text-xl text-gray-600 mb-8">
            Discover how Gramtek solutions can transform your constituency
          </p>
          <Button
            size="lg"
            className="bg-gradient-to-r from-cyan-500 to-cyan-600 hover:from-cyan-600 hover:to-cyan-700 text-white px-12 py-6 text-lg rounded-2xl"
            asChild
          >
            <Link to="/contact">Get in Touch</Link>
          </Button>
        </div>
      </section>
    </div>
  );
};

export default BlogPage;